import { toBooleanFlag } from '@/utils/dataNormalization';
import { formatCodeName, toNullableText } from '@/utils/textValue';
import { toPositiveOptionId } from '@/utils/optionValues';
import { normalizeCatalogOptions, normalizeCurrencyOptions } from '@/utils/catalogOptions';

export const sortExpenseFeeRowsByDateAsc = (rows) => {
  return [...rows].sort((left, right) => {
    const leftTime = new Date(left?.date || left?.created_at || 0).getTime();
    const rightTime = new Date(right?.date || right?.created_at || 0).getTime();

    return leftTime - rightTime;
  });
};

export const toSafeText = (value) => toNullableText(value);

export const toPositiveNumber = (value) => toPositiveOptionId(value);

export const normalizeExpenseFeeTags = (rawTags) => {
  return (Array.isArray(rawTags) ? rawTags : [])
    .map((tag) => {
      if (tag && typeof tag === 'object') {
        return {
          id: toPositiveNumber(tag.id),
          name: toSafeText(tag.name || tag.label || ''),
        };
      }

      return {
        id: 0,
        name: toSafeText(tag),
      };
    })
    .filter((tag) => tag.name !== '');
};

export const resolveExpenseFeeReference = (row) => {
  const candidates = [
    row?.payment?.code,
    row?.payment_receipt_code,
    row?.reference,
    row?.folio,
    row?.code,
    row?.expense_code,
  ];

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = toSafeText(candidates[index]);
    if (candidate !== '') {
      return candidate;
    }
  }

  return '';
};

export const resolveExpenseFeeFolio = (row) => {
  return toSafeText(row?.folio || row?.invoice_folio || '');
};

export const resolveExpenseFeeSaleName = (row) => {
  const sale = row?.sale && typeof row.sale === 'object' ? row.sale : {};
  const saleLabel = typeof row?.sale_label === 'string' ? row.sale_label : '';

  return formatCodeName(
    sale.code || row?.sale_code,
    sale.name || row?.sale_name,
    saleLabel,
  );
};

export const resolveExpenseFeeStatus = (
  row,
  {
    paidLabel = 'Pagado',
    pendingLabel = 'Pendiente',
  } = {}
) => {
  const explicitStatus = toSafeText(row?.status);
  if (explicitStatus !== '') {
    return explicitStatus;
  }

  const hasPaymentId = toPositiveNumber(row?.payment_id || row?.payment?.id) > 0;
  const paidByFlag = toBooleanFlag(row?.paid ?? row?.is_paid ?? row?.has_payment ?? false);

  return (hasPaymentId || paidByFlag) ? paidLabel : pendingLabel;
};

export const resolveExpenseFeeHasXml = (row) => {
  const hasExplicit = Object.prototype.hasOwnProperty.call(row || {}, 'has_xml');
  if (hasExplicit) {
    return toBooleanFlag(row?.has_xml);
  }

  const invoiceXml = toSafeText(row?.invoice_xml || row?.xml || row?.xml_url || row?.xml_file);
  return invoiceXml !== '';
};

export const resolveExpenseFeeHasPdf = (row) => {
  const hasExplicit = Object.prototype.hasOwnProperty.call(row || {}, 'has_pdf');
  if (hasExplicit) {
    return toBooleanFlag(row?.has_pdf);
  }

  const invoicePdf = toSafeText(row?.invoice_pdf || row?.pdf || row?.pdf_url || row?.pdf_file);
  return invoicePdf !== '';
};

export const resolveExpenseFeeHasReceipt = (row) => {
  const hasExplicit = Object.prototype.hasOwnProperty.call(row || {}, 'has_receipt');
  if (hasExplicit) {
    return toBooleanFlag(row?.has_receipt);
  }

  const receipt = toSafeText(
    row?.receipt
    || row?.voucher_file
    || row?.comprobante
    || row?.receipt_url
    || row?.voucher_url
  );

  return receipt !== '';
};

export const normalizeExpenseFeeTaxItems = (rawItems) => {
  return (Array.isArray(rawItems) ? rawItems : [])
    .map((item) => {
      const source = item && typeof item === 'object' ? item : {};

      return {
        direction: toSafeText(source.direction || 'trasladado').toLowerCase(),
        code: toSafeText(source.code || ''),
        name: toSafeText(source.name || ''),
        rate: Number(
          source.rate
          ?? source.percentage
          ?? source.percent
          ?? source.tasa_o_cuota
          ?? 0
        ) || 0,
        amount: Number(source.amount || 0) || 0,
      };
    })
    .filter((item) => item.amount > 0);
};

export const buildExpenseFeeHasXmlOptions = ({ yesLabel = 'Si', noLabel = 'No' } = {}) => {
  return [
    { value: '1', label: yesLabel },
    { value: '0', label: noLabel },
  ];
};

export const normalizeExpenseFeeCurrencyOptions = (items) => {
  return normalizeCurrencyOptions(items, { useCodeInLabel: false });
};
